import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import LogicFlow from '@logicflow/core'
import { Snapshot, DndPanel, SelectionSelect, Menu, Control, Group } from '@logicflow/extension'
import '@logicflow/core/dist/style/index.css'
import '@logicflow/extension/lib/style/index.css'
import { onMounted, Ref, ref, unref, watch } from 'vue'
import { ProcessModel, PropertyEvent } from './types'
import PropertSetting from './PropertySetting'
import EventEmitter from '@logicflow/core/types/event/eventEmitter'
import { SnakerFlowElement, SnakerFlowAdapter } from './snakerflow/index'
import { NodeTypeEnum } from './enums'
import DataDetail from './Control/DataDetail.vue'
import ImportData from './Control/ImportData.vue'
import HighLightData from './Control/HighLightData.vue'
import { snakerXml2LogicFlowJson } from './snakerflow/tool'

// 定义组件名
// eslint-disable-next-line no-undef



import { defineComponent as __MACROS_defineComponent } from "vue";
const __default__ = __MACROS_defineComponent({
  name: 'SnakerFlowDesigner'
});

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  modelValue: {
    type: [Object, String]
  },
  config: {
    type: Object,
    default () {
      return {
        grid: true
      }
    }
  },
  highLight: { // 高亮数据
    type: Object,
    default () {
      return {
      }
    }
  },
  viewer: { // 预览模式
    type: Boolean,
    default: false
  },
  extendAttrConfig: { // 扩展属性配置
    type: Object
  },
  allUser: {
    type: Array as any,
    default: () => []
  },
  deptList: {
    type: Array as any,
    default: () => []
  },
  roleList: {
    type: Array as any,
    default: () => []
  },
  posList: {
    type: Array as any,
    default: () => {
      return []
    }
  }
},
  emits: ['update:modelValue', 'on-save'],
  setup(__props, { expose: __expose, emit: __emit }) {

const emits = __emit

// 定义挂载元素Ref
const lfElRef:Ref = ref(null)
// 属性面板操作
const propertSettingRef: Ref = ref(null)
// 查看数据弹窗
const dataDetailRef: Ref = ref(null)
// 导入数据弹窗
const importDataRef: Ref = ref(null)
// 导入高高弹窗
const highLightDataRef: Ref = ref(null)
// 定义LogicFlow实例
const lfInstance = ref(null) as Ref<LogicFlow | null>
// 当前操作节点/或边id
const currentOpId = ref('')
// 定义组件接收的参数
const props = __props

// 监听流程变化
watch(() => props.modelValue, () => {
  reRender(props.modelValue)
}, {
  deep: true
})
// 监听高亮数据
watch(() => props.highLight, () => {
  setHighLight(props.highLight)
}, {
  deep: true
})
// 初始化
const init = () => {
  // 画布配置
  LogicFlow.use(Snapshot)
  LogicFlow.use(DndPanel)
  LogicFlow.use(SelectionSelect)
  LogicFlow.use(Menu)
  LogicFlow.use(Control)
  LogicFlow.use(SnakerFlowElement)
  LogicFlow.use(SnakerFlowAdapter)
  LogicFlow.use(Group)
  const defaultConfig: any = {}
  if (props.viewer) {
    defaultConfig.isSilentMode = true
  }
  lfInstance.value = new LogicFlow({
    container: unref(lfElRef),
    grid: true,
    ...props.config,
    ...defaultConfig
  })
  // 初始化操作
  initOp()
  reRender(props.modelValue as ProcessModel)
  // 初始化事件
  initEvent()
}
// 初始化操作
const initOp = () => {
  const lf = unref(lfInstance)
  if (!lf) return
  if (props.viewer) {
    // 预览模式时
    lf.extension.menu.setMenuConfig({
      nodeMenu: [],
      edgeMenu: []
    })
    // 删除上一步
    lf.extension.control.removeItem('undo')
    // 删除下一步
    lf.extension.control.removeItem('redo')
    return
  } else {
    lf.extension.menu.setMenuConfig({
      nodeMenu: [
        {
          text: '删除',
          callback (node: any) {
            lf.deleteNode(node.id)
          }
        }
      ]
    })
  }
  // 控制面板-清空画布
  lf.extension.control.addItem({
    iconClass: 'lf-control-clear',
    title: 'clear',
    text: '清空',
    onClick: () => {
      lf.clearData()
    }
  })
  // 控制面板-添加查看按钮
  lf.extension.control.addItem({
    iconClass: 'lf-control-see',
    title: '',
    text: '查看',
    onClick: () => {
      const dataDetailElRef = unref(dataDetailRef)
      if (!dataDetailElRef) return
      const graphData = getGraphData()
      dataDetailElRef.show(graphData)
    }
  })
  // 控制面板-添加导入按钮
  lf.extension.control.addItem({
    iconClass: 'lf-control-import',
    title: '',
    text: '导入',
    onClick: () => {
      unref(importDataRef).show()
    }
  })
  // 控制面板-设置高亮数据
  lf.extension.control.addItem({
    iconClass: 'lf-control-setting',
    title: '',
    text: '设置高亮',
    onClick: () => {
      unref(highLightDataRef).show()
    }
  })
  // 控制面板-保存
  lf.extension.control.addItem({
    iconClass: 'lf-control-save',
    title: '',
    text: '保存',
    onClick: () => {
      emits('on-save', getGraphData())
    }
  })
  // 设置默认边
  lf.setDefaultEdgeType('snaker:transition')
  // 设置拖拽面板
  lf.extension.dndPanel.setPatternItems([
    {
      type: 'snaker:start',
      text: '开始',
      label: '开始节点',
      properties: {},
      icon: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAAH6ji2bAAAABGdBTUEAALGPC/xhBQAAAnBJREFUOBGdVL1rU1EcPfdGBddmaZLiEhdx1MHZQXApraCzQ7GKLgoRBxMfcRELuihWKcXFRcEWF8HBf0DdDCKYRZpnl7p0svLe9Zzbd29eQhTbC8nv+9zf130AT63jvooOGS8Vf9Nt5zxba7sXQwODfkWpkbjTQfCGUd9gIp3uuPP8bZ946g56dYQvnBg+b1HB8VIQmMFrazKcKSvFW2dQTxJnJdQ77urmXWOMBCmXM2Rke4S7UAW+/8ywwFoewmBps2tu7mbTdp8VMOkIRAkKfrVawalJTtIliclFbaOBqa0M2xImHeVIfd/nKAfVq/LGnPss5Kh00VEdSzfwnBXPUpmykNss4lUI9C1ga+8PNrBD5YeqRY2Zz8PhjooIbfJXjowvQJBqkmEkVnktWhwu2SM7SMx7Cj0N9IC0oQXRo8xwAGzQms+xrB/nNSUWVveI48ayrFGyC2+E2C+aWrZHXvOuz+CiV6iycWe1Rd1Q6+QUG07nb5SbPrL4426d+9E1axKjY3AoRrlEeSQo2Eu0T6BWAAr6COhTcWjRaYfKG5csnvytvUr/WY4rrPMB53Uo7jZRjXaG6/CFfNMaXEu75nG47X+oepU7PKJvvzGDY1YLSKHJrK7vFUwXKkaxwhCW3u+sDFMVrIju54RYYbFKpALZAo7sB6wcKyyrd+aBMryMT2gPyD6GsQoRFkGHr14TthZni9ck0z+Pnmee460mHXbRAypKNy3nuMdrWgVKj8YVV8E7PSzp1BZ9SJnJAsXdryw/h5ctboUVi4AFiCd+lQaYMw5z3LGTBKjLQOeUF35k89f58Vv/tGh+l+PE/wG0rgfIUbZK5AAAAABJRU5ErkJggg=='
    },
    {
      type: 'snaker:task',
      text: '用户任务',
      label: '用户任务',
      properties: {},
      icon: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABMAAAATCAYAAAEFVwZaAAAABGdBTUEAALGPC/xhBQAAAqlJREFUOBF9VM9rE0EUfrMJNUKLihGbpLGtaCOIR8VjQMGDePCgCCIiCNqzCAp2MyYUCXhUtF5E0D+g1t48qAd7CCLqQUQKEWkStcEfVGlLdp/fm3aW2QQdyLzf33zz5m2IsAZ9XhDpyaaIZkTS4ASzK41TFao88GuJ3hsr2pAbipHxuSYyKRugagICGANkfFnNh3HeE2N0b3nN2cgnpcictw5veJIzxmDamSlxxQZicq/mflxhbaH8BLRbuRwNtZp0JAhoplVRUdzmCe/vO27wFuuA3S5qXruGdboy5/PRGFsbFGKo/haRtQHIrM83bVeTrOgNhZReWaYGnE4aUQgTJNvijJFF4jQ8BxJE5xfKatZWmZcTQ+BVgh7s8SgPlCkcec4mGTmieTP4xd7PcpIEg1TX6gdeLW8rTVMVLVvb7ctXoH0Cydl2QOPJBG21STE5OsnbweVYzAnD3A7PVILuY0yiiyDwSm2g441r6rMSgp6iK42yqroI2QoXeJVeA+YeZSa47gZdXaZWQKTrG93rukk/l2Al6Kzh5AZEl7dDQy+JjgFahQjRopSxPbrbvK7GRe9ePWBo1wcU7sYrFZtavXALwGw/7Dnc50urrHJuTPSoO2IMV3gUQGNg87IbSOIY9BpiT9HV7FCZ94nPXb3MSnwHn/FFFE1vG6DTby+r31KAkUktB3Qf6ikUPWxW1BkXSPQeMHHiW0+HAd2GelJsZz1OJegCxqzl+CLVHa/IibuHeJ1HAKzhuDR+ymNaRFM+4jU6UWKXorRmbyqkq/D76FffevwdCp+jN3UAN/C9JRVTDuOxC/oh+EdMnqIOrlYteKSfadVRGLJFJPSB/ti/6K8f0CNymg/iH2gO/f0DwE0yjAFO6l8JaR5j0VPwPwfaYHqOqrCI319WzwhwzNW/aQAAAABJRU5ErkJggg==',
      className: 'important-node'
    },
    {
      type: 'snaker:custom',
      text: '自定义任务',
      label: '自定义任务',
      properties: {},
      icon: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAAH6ji2bAAAABGdBTUEAALGPC/xhBQAAAOVJREFUOBGtVMENwzAIjKP++2026ETdpv10iy7WFbqFyyW6GBywLCv5gI+Dw2Bluj1znuSjhb99Gkn6QILDY2imo60p8nsnc9bEo3+QJ+AKHfMdZHnl78wyTnyHZD53Zzx73MRSgYvnqgCUHj6gwdck7Zsp1VOrz0Uz8NbKunzAW+Gu4fYW28bUYutYlzSa7B84Fh7d1kjLwhcSdYAYrdkMQVpsBr5XgDGuXwQfQr0y9zwLda+DUYXLaGKdd2ZTtvbolaO87pdo24hP7ov16N0zArH1ur3iwJpXxm+v7oAJNR4JEP8DoAuSFEkYH7cAAAAASUVORK5CYII=',
      className: 'import_icon'
    },
    {
      type: 'snaker:decision',
      label: '条件判断',
      properties: {},
      icon: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABUAAAAVCAYAAAHeEJUAAAAABGdBTUEAALGPC/xhBQAAAvVJREFUOBGNVEFrE0EU/mY3bQoiFlOkaUJrQUQoWMGePLX24EH0IIoHKQiCV0G8iE1covgLiqA/QTzVm1JPogc9tIJYFaQtlhQxqYjSpunu+L7JvmUTU3AgmTfvffPNN++9WSA1DO182f6xwILzD5btfAoQmwL5KJEwiQyVbSVZ0IgRyV6PTpIJ81E5ZvqfHQR0HUOBHW4L5Et2kQ6Zf7iAOhTFAA8s0pEP7AXO1uAA52SbqGk6h/6J45LaLhO64ByfcUzM39V7ZiAdS2yCePPEIQYvTUHqM/n7dgQNfBKWPjpF4ISk8q3J4nB11qw6X8l+FsF3EhlkEMfrjIer3wJTLwS2aCNcj4DbGxXTw00JmAuO+Ni6bBxVUCvS5d9aa04+so4pHW5jLTywuXAL7jJ+D06sl82Sgl2JuVBQn498zkc2bGKxULHjCnSMadBKYDYYHAtsby1EQ5lNGrQd4Y3v4Zo0XdGEmDno46yCM9Tk+RiJmUYHS/aXHPNTcjxcbTFna000PFJHIVZ5lFRqRpJWk9/+QtlOUYJj9HG5pVFEU7zqIYDVsw2s+AJaD8wTd2umgSCCyUxgGsS1Y6TBwXQQTFuZaHcd8gAGioE90hlsY+wMcs30RduYtxanjMGal8H5dMW67dmT1JFtYUEe8LiQLRsPZ6IIc7A4J5tqco3T0pnv/4u0kyzrYUq7gASuEyI8VXKvB9Odytv6jS/PNaZBln0nioJG/AVQRZvApOdhjj3Jt8QC8Im09SafwdBdvIpztpxWxpeKCC+EsFdS8DCyuCn2munFpL7ctHKp+Xc5cMybeIyMAN33SPL3ZR9QV1XVwLyzHm6Iv0/yeUuUb7PPlZC4D4HZkeu6dpF4v9j9MreGtMbxMMRLIcjJic9yHi7WQ3yVKzZVWUr5UrViJvn1FfUlwe/KYVfYyWRLSGNu16hR01U9IacajXPei0wx/5BqgInvJN+MMNtNme7ReU9SBbgntovn0kKHpFg7UogZvaZiOue/q1SBo9ktHzQAAAAASUVORK5CYII='
    },
    {
      type: 'snaker:fork',
      label: '分支',
      properties: {},
      icon: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAAAXNSR0IArs4c6QAAASlJREFUOE+tlL9Lw1AQx78X/GuSzJ2zugqddNPBwS3BJqWCmwnq5lKcXAVHN0EFJ0EQ2pfZ3U1FKohfeaWRYl/zniZvu+P43H3vxxO0/KQJz895+D7B3tO+TCpOI2CQkwAu6WFQ7sqjhjoB/YLrQmwZ1ERTH/EGYkf15cwKDAuukbiwtUaA4TiVbSswyHkNIFKpLMTOJL8ASFQqp06SLcArj+iNMnlYGIqfcxWCjveF23EmN1VAHdDUhqmMsOA5ie5PwAoClUip7T8DgyP6+IT6lU3bz3M+Yw+NFbYObF1yVXarQ6lb2n+vzTKow2K/AoidFzs8YESBvpba53x6s6FtkthY+jkAHySSMpMT6y1b+qu/rzsK4rIn9063bAEeq1Ti+ZhGFZqSfQNEXZkVkDFbFAAAAABJRU5ErkJggg=='
    },
    {
      type: 'snaker:join',
      label: '合并',
      properties: {},
      icon: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAAAXNSR0IArs4c6QAAASZJREFUOE+1lDFOw0AQRf83LW1SICGBRIHiUFEjkQNQkpZjYBeRkso+RQ4ALT0paJCgwnaBFFFxAkok/2gj2YrjdZyNkq12pdk382fnL7HnxT3zUAP2I92aJEnI2S7JKkA/1iuAJRDALA04cIWWwF6kexJPqwAJwyzkswu0BBqpIkyF5aIwcJV+OMlFWX6sPwDH/MdFMuLcRa6Jrb1yL9YvgRMR46McL18hP12gNaAfKwHRgdAtXrsCJL6V4z0LObUlsgMBSJjQwx2E07WLy7EyCrJHTtahjcA0YN9WwWWkM8/DA4WxbaxswB8DSgOeN/XuKtJ1TnwAeEsD3lRGbfWwrVM2xTk7pc1Rzk5pc9ROTtlKctFLI8ns2z6FprjDf7AuNrPFLgDE4owVZSkI4QAAAABJRU5ErkJggg=='
    },
    {
      type: 'snaker:end',
      text: '结束',
      label: '结束节点',
      properties: {},
      icon: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAAH6ji2bAAAABGdBTUEAALGPC/xhBQAAA1BJREFUOBFtVE1IVUEYPXOf+tq40Y3vPcmFIdSjIorWoRG0ERWUgnb5FwVhYQSl72oUoZAboxKNFtWiwKRN0M+jpfSzqJAQclHo001tKkjl3emc8V69igP3znzfnO/M9zcDcKT67azmjYWTwl9Vn7Vumeqzj1DVb6cleQY4oAVnIOPb+mKAGxQmKI5CWNJ2aLPatxWa3aB9K7/fB+/Z0jUF6TmMlFLQqrkECWQzOZxYGjTlOl8eeKaIY5yHnFn486xBustDjWT6dG7pmjHOJd+33t0iitTPkK6tEvjxq4h2MozQ6WFSX/LkDUGfFwfhEZj1Auz/U4pyAi5Sznd7uKzznXeVHlI/Aywmk6j7fsUsEuCGADrWARXXwjxWQsUbIupDHJI7kF5dRktg0eN81IbiZXiTESic50iwS+t1oJgL83jAiBupLDCQqwziaWSoAFSeIR3P5Xv5az00wyIn35QRYTwdSYbz8pH8fxUUAtxnFvYmEmgI0wYXUXcCCSpeEVpXlsRhBnCEATxWylL9+EKCAYhe1NGstUa6356kS9NVvt3DU2fd+Wtbm/+lSbylJqsqkSm9CRhvoJVlvKPvF1RKY/FcPn5j4UfIMLn8D4UYb54BNsilTDXKnF4CfTobA0FpoW/LSp306wkXM+XaOJhZaFkcNM82ASNAWMrhrUbRfmyeI1FvRBTpN06WKxa9BK0o2E4Pd3zfBBEwPsv9sQBnmLVbLEIZ/Xe9LYwJu/Er17W6HYVBc7vmuk0xUQ+pqxdom5Fnp55SiytXLPYoMXNM4u4SNSCFWnrVIzKG3EGyMXo6n/BQOe+bX3FClY4PwydVhthOZ9NnS+ntiLh0fxtlUJHAuGaFoVmttpVMeum0p3WEXbcll94l1wM/gZ0Ccczop77VvN2I7TlsZCsuXf1WHvWEhjO8DPtyOVg2/mvK9QqboEth+7pD6NUQC1HN/TwvydGBARi9MZSzLE4b8Ru3XhX2PBxf8E1er2A6516o0w4sIA+lwURhAON82Kwe2iDAC1Watq4XHaGQ7skLcFOtI5lDxuM2gZe6WFIotPAhbaeYlU4to5cuarF1QrcZ/lwrLaCJl66JBocYZnrNlvm2+MBCTmUymPrYZVbjdlr/BxlMjmNmNI3SAAAAAElFTkSuQmCC'
    }
    // {
    //   type: 'snaker:subProcess',
    //   text: '',
    //   label: '子流程',
    //   properties: {},
    //   icon: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAAAXNSR0IArs4c6QAAAiFJREFUOE+1lD9oU1EUxn/nviRVB0Hwz1SwJn0WXRylGbSTboJTKwgFwTrqoJK8lKQ0LxUEBR2UolRcDKJQVFxbFx10cNL2JSFFdHARN8Um92jSpqY2bUipdzz3vN/53vu+d4QtPrLFPNoC3XHNYDhXH2y5H4xKZiMRGwIP+vpMhZ+qvBSIqDAkymzgydh60HWB0ax2O8InNZwpJORRDeBmdQJhMPCkp2Ogm9EDhClZ4XAxKR/qwKXXPxl4crRj4LKiPMJHByYrcEIgrXCj4MntTQGj13Wv84s7wOk6QLkWpCSxaVMaD/bmdEaqvGrncK1/lSmxcT3earo4pGtAC7Ot7oujslJfAzSGmY7CbhlrVt4SGHiyql5zVx2OFZIy0Dys1af4v0A3q2cRHholPpeS1w01DYUVZbDsyddavcfXfSEh/69ZKwp7c3rTKFErlES5CAwFnuRdX98ovBfYBgzbEN2hH4SqEaaM8hboA3bPe9K/ymU3p1+MEJ9LyILr6xRKl6mQtBGmg6QcqQfd17IoVxR2APEgJef7JnS/tZRrg4pX5fOKQtfXd1hemBAPqpa7As/DizxdDDNvLaccYY8Kj61lwAi7VBhxDBdshWEM6YaRf4FL/+klYCcwXTVcLiWk6Gb1HrK8vmAy8GQkNq5RY3gC1JXTFJ012yaW00ONZdAwJXZLuyrf2L6Qke/NsenLqTuXlKC51nbBdhTyP82/ASr35RWxFgVJAAAAAElFTkSuQmCC',
    //   className: 'import_icon'
    // }
  ])
}
// 初始化事件
const initEvent = () => {
  if (props.viewer) return
  // 初始化事件
  const lf = unref(lfInstance)
  if (!lf) return
  // 空白区右键事件-弹出流程属性表单
  const eventCenter: EventEmitter = lf.graphModel.eventCenter
  // eventCenter.on('blank:contextmenu', (args) => {
  //   propertSettingRef.value.show({
  //     name: lf.graphModel.name,
  //     displayName: lf.graphModel.displayName,
  //     expireTime: lf.graphModel.expireTime,
  //     instanceUrl: lf.graphModel.instanceUrl,
  //     instanceNoClass: lf.graphModel.instanceNoClass,
  //     type: 'process'
  //   })
  // })
  // 节点点击事件
  eventCenter.on('node:click', (args) => {
    console.log('eventCenter', args)
    currentOpId.value = args.data.id
    if (!args.data.properties.field) {
      args.data.properties.field = '{}'
    }
    propertSettingRef.value.show({
      ...args.data.properties,
      name: args.data.id,
      displayName: args.data.text?.value,
      type: args.data.type
    })
  })
  // 边点击事件
  eventCenter.on('edge:click', (args) => {
    currentOpId.value = args.data.id
    propertSettingRef.value.show({
      ...args.data.properties,
      name: args.data.id,
      displayName: args.data.text?.value,
      type: args.data.type
    })
  })
}
// 重新渲染
const reRender = (data: any): void => {
  const lf = unref(lfInstance)
  if (!lf) return
  lf.render(data)
  // 设置高亮
  setHighLight(props.highLight)
}
// 处理属性值变化事件
const handlePropertyChange = (e: PropertyEvent) => {
  const lf = unref(lfInstance)
  if (!lf) return
  if (e.type === NodeTypeEnum.process) {
    // 流程属性
    lf.graphModel[e.propertyName] = e.propertyValue
  } else if (e.type === NodeTypeEnum.subProcess) {
    // 节点属性
    const nodeId = unref(currentOpId)
    if (e.propertyName === 'name') {
      // 更新唯一标识
      if (!lf.getNodeModelById(e.propertyValue)) {
        lf.changeNodeId(nodeId, e.propertyValue)
        currentOpId.value = e.propertyValue
      }
    } else {
      lf.setProperties(nodeId, {
        [e.propertyName]: e.propertyValue
      })
    }
  } else if (([
    NodeTypeEnum.custom,
    NodeTypeEnum.decision,
    NodeTypeEnum.end,
    NodeTypeEnum.fork,
    NodeTypeEnum.join,
    NodeTypeEnum.start,
    NodeTypeEnum.task
  ] as NodeTypeEnum []).includes(e.type)) {
    // 节点属性
    const nodeId = unref(currentOpId)
    // 节点信息
    if (e.propertyName === 'name') {
      // 更新唯一标识
      if (!lf.getNodeModelById(e.propertyValue)) {
        lf.changeNodeId(nodeId, e.propertyValue)
        currentOpId.value = e.propertyValue
      }
    } else if (e.propertyName === 'displayName') {
      // 更新节点文本值
      lf.updateText(nodeId, e.propertyValue)
    } else if (e.propertyName === 'width' && [NodeTypeEnum.task, NodeTypeEnum.custom].includes(e.type)) {
      // 宽度
      lf.setProperties(nodeId, {
        width: (Number.isNaN(e.propertyValue) ? 120 : e.propertyValue) as number
      })
      const nodeModel = lf.getNodeModelById(nodeId)
      if (nodeModel) {
        nodeModel.width = (Number.isNaN(e.propertyValue) ? 120 : e.propertyValue) as number
      }
    } else if (e.propertyName === 'height' && [NodeTypeEnum.task, NodeTypeEnum.custom].includes(e.type)) {
      // 高度
      lf.setProperties(nodeId, {
        height: (Number.isNaN(e.propertyValue) ? 120 : e.propertyValue) as number
      })
      const nodeModel = lf.getNodeModelById(nodeId)
      if (nodeModel) {
        nodeModel.height = (Number.isNaN(e.propertyValue) ? 120 : e.propertyValue) as number
      }
    } else if (e.propertyName === 'field') {
      // 更新扩展属性
      lf.setProperties(nodeId, {
        field: JSON.stringify(e.propertyValue)
      })
    } else {
      // 更新基础属性
      lf.setProperties(nodeId, {
        [e.propertyName]: e.propertyValue
      })
    }
    emits('update:modelValue', getGraphData())
  } else if (e.type === NodeTypeEnum.transition) {
    // 边属性
    const edgeId = unref(currentOpId)
    if (e.propertyName === 'name') {
      // 更新唯一标识
      if (!lf.getEdgeModelById(e.propertyValue)) {
        lf.changeEdgeId(edgeId, e.propertyValue)
        currentOpId.value = e.propertyValue
      }
    } else if (e.propertyName === 'displayName') {
      // 更新节点文本值
      lf.updateText(edgeId, e.propertyValue)
    } else {
      // 更新基础属性
      lf.setProperties(edgeId, {
        [e.propertyName]: e.propertyValue
      })
    }
  }
}
/**
 * 获取流程数据
 */
const getGraphData = () => {
  const lf = unref(lfInstance)
  if (!lf) return {}
  return lf.getGraphData()
}
/**
 * 处理导入提交事件
 */
const handleImportSubmit = (str: string) => {
  let data :any = null
  try {
    data = JSON.parse(str)
  } catch {
    try {
      data = snakerXml2LogicFlowJson(str)
    } catch {
      return
    }
  }
  if (data && data.nodes && data.edges) {
    reRender(data)
  }
}
// 导入json
const importJson = (data:any) => {
  reRender(data)
}
// 导入xml
const importXml = (xml:any) => {
  reRender(xml)
}
// 刷新导入下拉数据
const refreshImport = () => {
  return unref(importDataRef)?.refresh()
}
// 导入高亮数据
const handleHighLightSubmit = (jsonStr: string) => {
  let data: any = null
  try {
    data = JSON.parse(jsonStr)
  } catch {
  }
  setHighLight(data)
}
/**
 * 设置高亮数据
 * @param data { "historyNodeNames": [], "historyEdgeNames": [], "activeNodeNames": []}
*/
const setHighLight = (data: any) => {
  const lf = unref(lfInstance)
  if (!lf) return
  // 设置历史节点
  if (data && data.historyNodeNames) {
    data.historyNodeNames.forEach((nodeName: string) => {
      lf.setProperties(nodeName, { state: 'history' })
    })
  }
  // 设置当前节点
  if (data && data.activeNodeNames) {
    data.activeNodeNames.forEach((nodeName: string) => {
      lf.setProperties(nodeName, { state: 'active' })
    })
  }
  // 设置历史边
  if (data && data.historyEdgeNames) {
    data.historyEdgeNames.forEach((edgeName: string) => {
      const edgeModel = lf.getEdgeModelById(edgeName)
      if (edgeModel) {
        edgeModel.setProperties({ state: 'history' })
      }
    })
  }
}
onMounted(init)
// 导出提供给外部操作-$refs.xxx
__expose({
  getGraphData,
  refreshImport,
  importJson,
  importXml,
  setHighLight
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", {
      class: "m-snakerflow-designer",
      ref_key: "lfElRef",
      ref: lfElRef
    }, null, 512),
    _createVNode(_unref(PropertSetting), {
      ref_key: "propertSettingRef",
      ref: propertSettingRef,
      extendAttrConfig: props.extendAttrConfig,
      allUser: props.allUser,
      deptList: props.deptList,
      roleList: props.roleList,
      posList: props.posList,
      onChange: handlePropertyChange
    }, null, 8, ["extendAttrConfig", "allUser", "deptList", "roleList", "posList"]),
    _createVNode(DataDetail, {
      ref_key: "dataDetailRef",
      ref: dataDetailRef
    }, null, 512),
    _createVNode(ImportData, {
      ref_key: "importDataRef",
      ref: importDataRef,
      onOnSubmit: handleImportSubmit
    }, null, 512),
    _createVNode(HighLightData, {
      ref_key: "highLightDataRef",
      ref: highLightDataRef,
      onOnSubmit: handleHighLightSubmit
    }, null, 512)
  ], 64))
}
}

})